import { type DefaultError, keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { createFolder, deleteFolder, getFolder, unDeleteFolder } from '@/services/folder.service';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type CreateFolderParams, type Folder } from '@/types/folder';
import { ToastAction, ToastVariant } from '@/components/ui/toast';
import { useCommandContext } from '@/context/CommandContext';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useFolder = (id?: string, options?: object) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { undo } = useCommandContext();

  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const getFolderWithAuth = useAuthenticatedQueryFn(getFolder);
  const createFolderWithAuth = useAuthenticatedQueryFn(createFolder);
  const authUnDeleteFolder = useAuthenticatedQueryFn(unDeleteFolder);
  const authDeleteFolder = useAuthenticatedQueryFn(deleteFolder);

  const {
    data: folder,
    isLoading: isFolderLoading,
    isPending: isFolderPending,
    isFetching: isFolderFetching,
    error: folderError,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.Folder, id],
    queryFn: id ? () => getFolderWithAuth(id) : () => undefined,
    placeholderData: keepPreviousData,
    enabled: false,
    ...options,
  });

  const { mutateAsync: createFolderMutation } = useMutation<Folder, DefaultError, CreateFolderParams>({
    mutationFn: createFolderWithAuth,
    onError: (err) => {
      errorLog('Action failed:', err);
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
    onSuccess: (response) => {
      toast({
        title: tToasts('createFolderMutation.title'),
        description: tToasts('createFolderMutation.description'),
        action: (
          <ToastAction onClick={undo} altText={tToasts('createFolderMutation.undoAlt')} icon={Undo2}>
            {tCommon('undo')}
          </ToastAction>
        ),
      });

      let path = [response.slug];
      if (response.ancestors && response.ancestors.length > 0) {
        const ancestorSlugs = response.ancestors?.map((ancestor) => ancestor.slug);
        path = [...ancestorSlugs, ...path];
      }
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.TreePath, path.join('/')],
        refetchType: 'all',
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void invalidateAssetDataForNodeChange(queryClient);

      return response;
    },
  });

  const { mutateAsync: unDeleteFolderMutation } = useMutation<
    Folder,
    DefaultError,
    Parameters<typeof unDeleteFolder>['0']
  >({
    mutationFn: (params) => authUnDeleteFolder(params),
    onSettled: () => {
      toast({
        title: tToasts('unDeleteFolderMutation.title'),
        description: tToasts('unDeleteFolderMutation.description'),
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void invalidateAssetDataForNodeChange(queryClient);
    },
  });

  const { mutateAsync: deleteFolderMutation } = useMutation<Folder, DefaultError, Parameters<typeof deleteFolder>['0']>(
    {
      mutationFn: (params) => authDeleteFolder(params),
      onSettled: () => {
        toast({
          title: tToasts('deleteFolderMutation.title'),
          description: tToasts('deleteFolderMutation.description'),
        });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
        void invalidateAssetDataForNodeChange(queryClient);
      },
    },
  );

  return {
    folder,
    refetchFolder: refetch,
    isFolderFetching,
    isFolderLoading,
    isFolderPending,
    error: folderError,
    createFolderMutation,
    unDeleteFolderMutation,
    deleteFolderMutation,
  };
};
